import { z } from "zod";
import { isCatalogDay } from "../utils/functions/catalogVersions.js";
import { IK_NUMBER_HINT, isValidIkNumber, normalizeIkNumber } from "../utils/functions/ikNumber.js";
import { INTERNAL_NUMBER_HINT, isValidInternalNumber, normalizeInternalNumber, } from "../utils/functions/internalNumber.js";
// Password requirements
const PASSWORD_MIN_LENGTH = 8;
/**
 * IK-number field. Canonicalises the value (uppercase `T`/`X` markers, stray characters
 * dropped) and then rejects anything that is not 9 digits — `validateRequest` writes the
 * parsed body back onto `req`, so services receive the normalized form.
 *
 * The SPA sanitizes as the user types, but that is a convenience, not a gate: this is the
 * only check that holds regardless of which client (or import) does the write.
 */
const ikNumberField = z
    .string({ invalid_type_error: IK_NUMBER_HINT })
    .transform(value => normalizeIkNumber(value) ?? "")
    .refine(isValidIkNumber, { message: IK_NUMBER_HINT });
/** Same rule, but tolerating an absent or blank value on partial updates. */
const optionalIkNumberField = z
    .string({ invalid_type_error: IK_NUMBER_HINT })
    .transform(value => normalizeIkNumber(value) ?? "")
    .refine(value => value === "" || isValidIkNumber(value), { message: IK_NUMBER_HINT })
    .nullish();
/**
 * Auth validators
 */
export const loginSchema = z.object({
    email: z.string().trim().min(1, "Email is required"),
    password: z.string().min(1, "Password is required"),
});
// POST /auth/register — { account, company } as SignUp.js sends it. The company carries more
// fields (billing address, bank data, extra IK numbers); they pass through and register.js picks
// what it stores. Rules are no stricter than the sign-up form's own checks.
const requiredText = (message) => z.string().trim().min(1, message);
// A password an account is given: at sign-up, from a reset link, or changed in the portal
const newPasswordField = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein`);
// 0 Herr, 1 Frau, 2 divers
const genderField = z
    .number({ required_error: "Anrede ist erforderlich", invalid_type_error: "Ungültige Anrede" })
    .int()
    .min(0)
    .max(2);
export const registerRequestSchema = z.object({
    account: z.object({
        email: z.string().trim().email("Ungültige E-Mail-Adresse"),
        password: newPasswordField,
        firstName: requiredText("Vorname ist erforderlich"),
        lastName: requiredText("Nachname ist erforderlich"),
        gender: genderField,
    }),
    company: z
        .object({
        name: requiredText("Unternehmensname ist erforderlich"),
        ik_number: ikNumberField,
        street: requiredText("Straße ist erforderlich"),
        street_number: requiredText("Hausnummer ist erforderlich"),
        postcode: requiredText("Postleitzahl ist erforderlich"),
        city: requiredText("Ort ist erforderlich"),
    })
        .passthrough(),
});
// POST /auth/check-email and POST /auth/send-password-reset-request — just an address. Their rate
// limits key on it, so it has to be a string.
export const emailRequestSchema = z.object({
    email: z.string().trim().min(1, "Bitte eine E-Mail-Adresse angeben"),
});
// POST /auth/accept-password-reset — the token from the reset link names the account
export const passwordResetSchema = z.object({
    token: z.string().min(1, "Der Link ist unvollständig"),
    password: newPasswordField,
});
// POST /auth/send-password-update — always the logged-in account's own password
export const passwordUpdateSchema = z.object({
    oldPassword: z.string().min(1, "Das alte Passwort fehlt"),
    newPassword: newPasswordField,
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
        .string()
        .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`),
    confirmPassword: z.string().min(1, "Confirm password is required"),
})
    .refine(data => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
});
/**
 * User validators
 */
export const userSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.enum(["admin", "user"]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const userProfileSchema = z.object({
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    phoneNumber: z.string().optional(),
    address: z.string().optional(),
    role: z.enum(["admin", "user"]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Product validators
 */
export const productSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1, "Product name is required"),
    description: z.string(),
    price: z.number().positive("Price must be positive"),
    category: z.string(),
    imageUrl: z.string().url().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createProductSchema = z.object({
    name: z.string().min(1, "Product name is required"),
    description: z.string(),
    price: z.number().positive("Price must be positive"),
    category: z.string(),
    imageUrl: z.string().url().optional(),
});
export const updateProductSchema = z.object({
    name: z.string().min(1, "Product name is required").optional(),
    description: z.string().optional(),
    price: z.number().positive("Price must be positive").optional(),
    category: z.string().optional(),
    imageUrl: z.string().url().optional(),
});
/**
 * Notes validators
 *
 * The `type`/`status` enums mirror the Prisma `NotesType` / `StatusType` enums. The shared package
 * can't import the generated Prisma client, so these literal values are kept in sync by hand — if
 * you add a value to either Prisma enum, add it here too.
 */
const noteTypeSchema = z.enum(["REMINDER", "TASK"]);
const statusTypeSchema = z.enum([
    "CREATED",
    "PENDING",
    "COMPLETED",
    "FAILED",
    "CANCELED",
    "IN_PROGRESS",
    "UNDER_REVIEW",
    "REJECTED",
    "ACCEPTED",
    "DOCUMENT_REVIEW",
    "MONITORING",
    "INSOLVENCY",
]);
// dueDate arrives as a JSON string ("YYYY-MM-DD" or an ISO instant). Validate that it parses, but
// keep it a string so the service's `new Date(dueDate)` keeps working unchanged (no coercion).
const dueDateSchema = z
    .string()
    .refine(value => !Number.isNaN(Date.parse(value)), "Invalid due date");
// POST /notes/createNotes — body is { note: {...} }
export const createNotesSchema = z.object({
    note: z
        .object({
        id: z.string().min(1).optional(),
        message: z.string().min(1, "Message is required"),
        dueDate: dueDateSchema,
        type: noteTypeSchema.optional(),
        status: statusTypeSchema.optional(),
        creatorId: z.string().min(1, "Creator ID is required"),
        responsibleId: z.string().min(1, "Responsible ID is required"),
        facilityId: z.string().min(1).nullish(),
        companyId: z.string().min(1).nullish(),
    })
        // passthrough(): only validate the fields we know; never silently drop extras the client sends.
        .passthrough()
        .refine(note => Boolean(note.facilityId) || Boolean(note.companyId), {
        message: "Either Facility ID or Company ID is required",
        path: ["facilityId"],
    }),
});
// POST /notes/updateNotes — body is { note: { noteId, ...partial } }
export const updateNotesSchema = z.object({
    note: z
        .object({
        noteId: z.string().min(1, "Note ID is required"),
        message: z.string().min(1).optional(),
        dueDate: dueDateSchema.optional(),
        type: noteTypeSchema.optional(),
        status: statusTypeSchema.optional(),
        responsibleId: z.string().min(1).optional(),
        facilityId: z.string().min(1).nullish(),
        companyId: z.string().min(1).nullish(),
    })
        .passthrough(),
});
// POST /notes/deleteNotes — body is { noteId }
export const deleteNotesSchema = z.object({
    noteId: z.string().min(1, "Note ID is required"),
});
// POST /notes/updateNoteStatus — body is { noteId, status }
export const updateNoteStatusSchema = z.object({
    noteId: z.string().min(1, "Note ID is required"),
    status: statusTypeSchema,
});
// POST /notes/getNotes — body is { includeOlder? }
export const getNotesSchema = z.object({
    includeOlder: z.boolean().optional(),
});
/**
 * ActionList validators
 *
 * actionList and account IDs are String(36) UUIDs; the *-multiple endpoints take plain arrays of
 * those IDs. `value` on the status route is a Prisma StatusType (reuses statusTypeSchema above).
 */
const actionListIdField = z.string().min(1, "Action list ID is required");
const accountIdField = z.string().min(1, "Account ID is required");
const actionListIdArrayField = z
    .array(z.string().min(1))
    .min(1, "At least one action list ID is required");
// { accountId, actionListId } — POST /attach and POST /move
export const accountActionPairSchema = z.object({
    accountId: accountIdField,
    actionListId: actionListIdField,
});
// { accountId, actionListIdArray } — POST /attach-multiple and POST /move-multiple
export const accountActionArraySchema = z.object({
    accountId: accountIdField,
    actionListIdArray: actionListIdArrayField,
});
// { actionListId } — POST /detach
export const detachActionSchema = z.object({
    actionListId: actionListIdField,
});
// { actionListItemArray } — POST /detach-multiple
export const detachMultipleActionsSchema = z.object({
    actionListItemArray: z
        .array(z.string().min(1))
        .min(1, "At least one action list item is required"),
});
// { id } — POST /item
export const getSpecificActionItemSchema = z.object({
    id: z.string().min(1, "Action list item ID is required"),
});
// params { accountId } — GET /account/:accountId
export const accountIdParamSchema = z.object({
    accountId: accountIdField,
});
// query { accountId } or { central: "true" } — GET /completed: the completed items of one
// person's list, or of the central list (no account). Neither returns every completed item, as
// SPA builds from before the split still ask.
export const completedActionListQuerySchema = z
    .object({
    accountId: accountIdField.optional(),
    central: z.literal("true").optional(),
})
    .refine(query => !(query.accountId && query.central), {
    message: "Pass accountId or central, not both",
});
// { actionListItemId, value } — POST /status
export const updateActionListItemStatusSchema = z.object({
    actionListItemId: z.string().min(1, "Action list item ID is required"),
    value: statusTypeSchema,
});
/**
 * Facility validators
 *
 * `objectType` (close/reopen/delete) is "company" | "facility"; `targetStatus` (reopen) is a Prisma
 * StatusType. Date fields arrive as JSON strings and are validated-but-not-coerced (the services
 * call `new Date(...)`). The `facility`/`company` object payloads are validated only for
 * presence/shape — their full structure stays owned by the service/Prisma layer.
 */
const facilityIdField = z.string().min(1, "Facility ID is required");
const objectTypeSchema = z.enum(["company", "facility"]);
const dateStringSchema = z
    .string()
    .refine(value => !Number.isNaN(Date.parse(value)), "Invalid date");
// An object payload that must at least carry an `id` (e.g. a facility/company reference).
const entityRefSchema = z.object({ id: z.string().min(1) }).passthrough();
// POST /facilities/getFacilityListForCompany — { companyId }
export const getFacilityListForCompanySchema = z.object({
    companyId: z.string().min(1, "Company ID is required"),
});
// POST /facilities/specific — { id }
export const getSpecificFacilitySchema = z.object({
    id: facilityIdField,
});
// POST /facilities/listByAccount — { id } (account ID)
export const getFacilitiesByAccountSchema = z.object({
    id: z.string().min(1, "Account ID is required"),
});
// POST /facilities/create — { facility, companyId? }
export const createFacilitySchema = z.object({
    facility: z.object({ ik_number: ikNumberField }).passthrough(),
    companyId: z.string().min(1).nullish(),
});
// POST /facilities/update — { id, company } (company is the facility payload)
export const updateFacilitySchema = z.object({
    id: facilityIdField,
    company: z.object({ ik_number: optionalIkNumberField }).passthrough(),
});
/**
 * A facility's internal number (utils/functions/internalNumber.ts). Checked, then handed on in
 * its stored form; blank or `null` becomes `null`, which removes the number.
 */
const internalNumberField = z
    .string({ invalid_type_error: INTERNAL_NUMBER_HINT })
    .nullable()
    .refine(isValidInternalNumber, { message: INTERNAL_NUMBER_HINT })
    .transform(normalizeInternalNumber);
// POST /facilities/edit — { facility }
export const editFacilitySchema = z.object({
    facility: z
        .object({
        ik_number: optionalIkNumberField,
        internal_number: internalNumberField.optional(),
    })
        .passthrough(),
});
// POST /facilities/setInternalNumber — { facilityId, internalNumber } (null or "" removes it)
export const setInternalNumberSchema = z.object({
    facilityId: facilityIdField,
    internalNumber: internalNumberField,
});
// POST /facilities/delete — { facility } (facility is the ID string)
export const deleteFacilitySchema = z.object({
    facility: facilityIdField,
});
// POST /facilities/createCancellation, /createTermination and /suspensionPreview — { facilityId }
export const facilityIdBodySchema = z.object({
    facilityId: facilityIdField,
});
// POST /facilities/createSuspension — { facilityId, startDate, endDate, reason, sendXMLToGKV? }
export const createSuspensionSchema = z.object({
    facilityId: facilityIdField,
    startDate: dateStringSchema,
    endDate: dateStringSchema,
    reason: z.string().min(1, "Reason is required"),
    sendXMLToGKV: z.boolean().optional(),
});
// POST /changes/closeInsolvency — the day a facility's insolvency proceedings ended (not in the
// future, not before they opened: the service checks that). While an insolvency administrator is
// its main contact, `mainContactId` names who takes over.
export const closeInsolvencySchema = z
    .object({
    facilityId: facilityIdField,
    date: z
        .string({ required_error: "Datum fehlt" })
        .refine(isCatalogDay, "Datum im Format JJJJ-MM-TT erwartet"),
    mainContactId: z.string().min(1).optional(),
})
    .strict();
// POST /facilities/createWithdrawal — suspensionDate is required only when isAfterSuspension is true
export const createWithdrawalSchema = z
    .object({
    facilityId: facilityIdField,
    terminationDate: dateStringSchema,
    suspensionDate: dateStringSchema.nullish(),
    isAfterSuspension: z.boolean().optional(),
    createEndInvoice: z.boolean(),
    sendXMLToGKV: z.boolean().optional(),
})
    .refine(data => !data.isAfterSuspension || Boolean(data.suspensionDate), {
    message: "suspensionDate is required when isAfterSuspension is true",
    path: ["suspensionDate"],
});
// POST /facilities/moveFacilityToCompany — { facility, company } (both objects carrying an id)
export const moveFacilityToCompanySchema = z.object({
    facility: entityRefSchema,
    company: entityRefSchema,
});
// POST /facilities/makeFacilityIndependent — { facility } (object carrying an id)
export const makeFacilityIndependentSchema = z.object({
    facility: entityRefSchema,
});
// POST /facilities/close and /deleteFacility — { objectId, objectType }
export const facilityObjectSchema = z.object({
    objectId: z.string().min(1, "Object ID is required"),
    objectType: objectTypeSchema,
});
// POST /facilities/reopen — { objectId, objectType, targetStatus }
export const reopenFacilitySchema = z.object({
    objectId: z.string().min(1, "Object ID is required"),
    objectType: objectTypeSchema,
    targetStatus: statusTypeSchema,
});
/**
 * Contactperson validators (user-facing messages are German, matching the rest of the domain).
 */
// POST /contactpersons/list — { ik_number? } (optional filter; service lists all when absent)
export const contactpersonListSchema = z.object({
    ik_number: z.union([z.string(), z.number()]).nullish(),
});
// PUT /contactpersons/update/:id — params { id }
export const contactpersonIdParamSchema = z.object({
    id: z.string().min(1, "ID der Kontaktperson ist erforderlich"),
});
// POST /contactpersons/delete — { contactPersonId }
export const deleteContactpersonSchema = z.object({
    contactPersonId: z.string().min(1, "Fehlerhafter Input: ID der Kontaktperson ist erforderlich."),
});
// POST /contactpersons/create — { facilityId, first_name, last_name, email, phone_number, ... }
export const createContactpersonSchema = z
    .object({
    facilityId: z.string().min(1, "Betriebsstätte ist erforderlich"),
    first_name: z.string().min(1, "Vorname ist erforderlich"),
    last_name: z.string().min(1, "Nachname ist erforderlich"),
    email: z.string().trim().email("Ungültige E-Mail-Adresse"),
    phone_number: z.string().min(1, "Telefonnummer ist erforderlich"),
})
    .passthrough();
/**
 * Email validators
 *
 * `type` is sent either as a string ("monitoring"/"assessment"/…) or a number, so it's a union.
 * `dueDate` is kept a loose non-empty string (it can arrive pre-formatted), not a parsed date.
 */
const emailTypeField = z.union([z.string().min(1), z.number()]);
// POST /email/get-eml — { id }
export const getEmlSchema = z.object({
    id: z.string().min(1, "id is required"),
});
// POST /email/more-info — { prequalificationId, message? }
export const moreInfoEmailSchema = z.object({
    prequalificationId: z.string().min(1, "prequalificationId is required"),
    message: z.string().optional(),
});
// POST /email/send-custom — { facilityId, message, subject }
export const sendCustomEmailSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
    message: z.string().min(1, "message is required"),
    subject: z.string().min(1, "subject is required"),
});
// POST /email/sendDeadlineReminderEmail — { facilityId, type?, noteId? }
export const sendDeadlineReminderEmailSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
    type: emailTypeField.optional(),
    noteId: z.string().nullish(),
});
// POST /email/sendMissingRequirementsEmail — { facilityId, dueDate, type }
export const sendMissingRequirementsEmailSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
    dueDate: z.string().min(1, "dueDate is required"),
    type: emailTypeField,
});
// POST /email/sendMissingAssessmentRequirementsEmail — { facilityId, dueDate }
export const sendMissingAssessmentRequirementsEmailSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
    dueDate: z.string().min(1, "dueDate is required"),
});
// POST /email/sendIncompleteDocumentsEmail — { facilityId, type }
export const sendIncompleteDocumentsEmailSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
    type: emailTypeField,
});
/**
 * Company validators (German user-facing messages, matching the domain).
 *
 * Like facility, the `company` field is an object on create/edit/update but an ID string on delete.
 */
const companyIdMessage = "Unternehmens-ID ist erforderlich";
// POST /companies/create — { company, accountId }
export const createCompanySchema = z.object({
    company: z.object({ ik_number: ikNumberField }).passthrough(),
    accountId: z.string().min(1, "Account-ID ist erforderlich"),
});
// body { id } — POST /companies/get and POST /companies/getContactPerson
export const companyIdBodySchema = z.object({
    id: z.string().min(1, companyIdMessage),
});
// params { id } — GET /companies/specific/:id
export const companyIdParamSchema = z.object({
    id: z.string().min(1, companyIdMessage),
});
// POST /companies/edit — { company }
export const editCompanySchema = z.object({
    company: z.object({ ik_number: optionalIkNumberField }).passthrough(),
});
// POST /companies/update — { id, company }
export const updateCompanySchema = z.object({
    id: z.string().min(1, companyIdMessage),
    company: z.object({ ik_number: optionalIkNumberField }).passthrough(),
});
// POST /company/changeMainHouse — { companyId, newMainHouseFacilityId }
export const changeMainHouseSchema = z.object({
    companyId: z.string().min(1, companyIdMessage),
    newMainHouseFacilityId: z.string().min(1, "ID der neuen Hauptbetriebsstätte ist erforderlich"),
});
/**
 * Criteria catalog validators: catalog versions and the entries (Katalogeinträge, Anforderungen,
 * Berufe) edited inside one of them.
 *
 * Entries are shared between versions until one of them changes. Every write therefore names the
 * version it is made in (`catalogId`) and how far it reaches (`scope`): "version" changes only
 * that version, "withNewer" also the newer versions that still share the entry. Older versions
 * never change. Validity bounds travel as "YYYY-MM-DD" calendar days.
 */
const catalogIdField = z.string().min(1, "Katalogversion fehlt");
const catalogEntryIdField = z.string().min(1, "Eintrag fehlt");
const catalogScopeField = z.enum(["version", "withNewer"]).default("version");
const catalogDayField = z
    .string({ required_error: "Datum fehlt" })
    .refine(isCatalogDay, "Datum im Format JJJJ-MM-TT erwartet");
const internalVersionField = z
    .string()
    .trim()
    .regex(/^\d+$/, "Interne Version: nur Ziffern, z. B. 19");
const officialVersionField = z
    .string()
    .trim()
    .regex(/^\d+(\.\d+)+$/, "Offizielle Version wie 1.0.14 erwartet");
// { catalogId } — POST /catalog-versions/specific and /delete
export const catalogVersionIdSchema = z.object({ catalogId: catalogIdField });
// POST /catalog-versions/duplicate — a new version holding the source version's entries
export const duplicateCatalogVersionSchema = z
    .object({
    sourceCatalogId: catalogIdField,
    version: internalVersionField,
    versionOfficial: officialVersionField,
    validFrom: catalogDayField,
    expires: catalogDayField,
    // Ends the source version the day before the new one starts
    endSource: z.boolean().default(false),
})
    .refine(body => body.validFrom <= body.expires, {
    message: "„Gültig bis“ liegt vor „Gültig ab“",
    path: ["expires"],
});
// POST /catalog-versions/update — version numbers and validity, each optional
export const updateCatalogVersionSchema = z.object({
    catalogId: catalogIdField,
    version: internalVersionField.optional(),
    versionOfficial: officialVersionField.optional(),
    validFrom: catalogDayField.optional(),
    expires: catalogDayField.optional(),
});
// { catalogId, id } — POST /catalog-items/remove, /requirements/remove, /occupations/remove
export const removeCatalogEntrySchema = z.object({
    catalogId: catalogIdField,
    id: catalogEntryIdField,
});
const catalogItemFields = {
    code: z.string().trim().min(1, "Code ist erforderlich").max(20, "Code ist zu lang"),
    description: z.string().trim().min(1, "Beschreibung ist erforderlich"),
    specialist_short_forms: z.string().trim(),
    productGroup: z.string().trim().nullable(),
    in_store_allowed: z.boolean(),
    field_sales_allowed: z.boolean(),
    inspection_relevant: z.boolean(),
};
// POST /catalog-items/create — a new entry in one version
export const createCatalogItemSchema = z.object({
    catalogId: catalogIdField,
    ...catalogItemFields,
    specialist_short_forms: catalogItemFields.specialist_short_forms.default(""),
    productGroup: catalogItemFields.productGroup.default(null),
    in_store_allowed: catalogItemFields.in_store_allowed.default(false),
    field_sales_allowed: catalogItemFields.field_sales_allowed.default(false),
    inspection_relevant: catalogItemFields.inspection_relevant.default(false),
});
// POST /catalog-items/update — { catalogId, id, scope, ...changed fields }
export const updateCatalogItemSchema = z
    .object({ catalogId: catalogIdField, id: catalogEntryIdField, scope: catalogScopeField })
    .extend(z.object(catalogItemFields).partial().shape);
// POST /catalog-items/connect-requirement and /disconnect-requirement
export const catalogItemRequirementSchema = z.object({
    catalogId: catalogIdField,
    catalogItemId: catalogEntryIdField,
    requirementId: catalogEntryIdField,
    scope: catalogScopeField,
});
// POST /catalog-items/field-sales-exclusion — a linked requirement not asked of field sales only
export const catalogItemFieldSalesSchema = catalogItemRequirementSchema.extend({
    excluded: z.boolean({ required_error: "excluded fehlt" }),
});
const requirementFields = {
    code: z.string().trim().min(1, "Kurzform ist erforderlich").max(20, "Kurzform ist zu lang"),
    description: z.string().trim().min(1, "Beschreibung ist erforderlich"),
};
/**
 * Requirement validators — id is a String(36).
 */
// POST /requirements/specific — { id }
export const getRequirementSchema = z.object({
    id: z.string().min(1, "Requirement ID is required"),
});
// POST /requirements/create — a new requirement in one version
export const createRequirementSchema = z.object({
    catalogId: catalogIdField,
    ...requirementFields,
});
// POST /requirements/update — { catalogId, id, scope, ...changed fields }
export const updateRequirementSchema = z
    .object({ catalogId: catalogIdField, id: catalogEntryIdField, scope: catalogScopeField })
    .extend(z.object(requirementFields).partial().shape);
const occupationFields = {
    short_form: z.string().trim().min(1, "Kürzel ist erforderlich").max(20, "Kürzel ist zu lang"),
    description: z.string().trim().min(1, "Beschreibung ist erforderlich"),
    certificate: z.string().trim(),
};
/**
 * Occupation validators — id is a String(36).
 */
// POST /occupations/specific — { id }
export const getOccupationSchema = z.object({
    id: z.string().min(1, "Occupation ID is required"),
});
// POST /occupations/create — a new occupation in one version
export const createOccupationSchema = z.object({
    catalogId: catalogIdField,
    ...occupationFields,
    certificate: occupationFields.certificate.default(""),
});
// POST /occupations/update — { catalogId, id, scope, ...changed fields }
export const updateOccupationSchema = z
    .object({ catalogId: catalogIdField, id: catalogEntryIdField, scope: catalogScopeField })
    .extend(z.object(occupationFields).partial().shape);
/**
 * History validators — history/facility/user IDs are String(36); update-status `value` is a Prisma
 * StatusType. `data` on create is optional (the service defaults it to { message: "" } when absent).
 */
const historyIdField = z.string().min(1, "History ID is required");
// POST /history/get — { facilityId }
export const getHistorySchema = z.object({
    facilityId: z.string().min(1, "Facility ID is required"),
});
// POST /history/create — { facilityId, data? }
export const createHistorySchema = z.object({
    facilityId: z.string().min(1, "Facility ID is required"),
    data: z.object({}).passthrough().optional(),
});
// POST /history/edit-time — { historyId, date }
export const editHistoryTimeSchema = z.object({
    historyId: historyIdField,
    date: z.string().min(1, "Date is required"),
});
// POST /history/update-status — { historyId, value }
export const updateHistoryStatusSchema = z.object({
    historyId: historyIdField,
    value: statusTypeSchema,
});
// POST /history/accept-file-upload and /history/delete — { historyId }
export const historyIdBodySchema = z.object({
    historyId: historyIdField,
});
// POST /history/edit — { historyId, message } (message must be a string; empty is allowed)
export const editHistorySchema = z.object({
    historyId: historyIdField,
    message: z.string({ required_error: "Nothing to update (message missing)." }),
});
// POST /history/change-user — { historyId, targetUserId }
export const changeHistoryUserSchema = z.object({
    historyId: historyIdField,
    targetUserId: z.string().min(1, "Target user ID is required"),
});
/**
 * Inspection validators
 *
 * inspection IDs are String(36); update-status `newStatus` is a Prisma StatusType. Date fields use
 * the "YYYY-MM-DD" wire format the SPA sends — the schema validates the shape, while the
 * "date not in the past" business rule stays in the controller (it needs the parsed moment).
 */
const inspectionIdField = z.string().min(1, "Valid inspection ID is required");
const ymdDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid inspection date format");
// { inspectionId } — POST /specific, /cancel, /accept-completion, /resend-auditplan
export const inspectionIdBodySchema = z.object({
    inspectionId: inspectionIdField,
});
// POST /inspection/create — { details } (service requires details.preqId + details.walkerId)
export const createInspectionSchema = z.object({
    details: z
        .object({
        preqId: z.string().min(1, "preqId is required"),
        walkerId: z.string().min(1, "walkerId is required"),
    })
        .passthrough(),
});
// POST /inspection/specific-list — { walkerId, loadOld, weeksBack?, startDate?, endDate? }
// Time filter: the last `weeksBack` weeks (0 = none) or an inclusive "YYYY-MM-DD" range, which
// wins when both are sent. The range bounds come as a pair; equal bounds select a single day.
export const specificInspectionListSchema = z
    .object({
    walkerId: z.string().min(1, "Valid walker ID is required"),
    loadOld: z.boolean({ invalid_type_error: "loadOld must be a boolean value" }),
    weeksBack: z.number().min(0, "weeksBack must be a non-negative number").default(0),
    startDate: ymdDateSchema.optional(),
    endDate: ymdDateSchema.optional(),
})
    .refine(b => Boolean(b.startDate) === Boolean(b.endDate), {
    message: "startDate and endDate must be sent together",
    path: ["endDate"],
})
    .refine(b => !b.startDate || !b.endDate || b.startDate <= b.endDate, {
    message: "endDate must not be before startDate",
    path: ["endDate"],
});
// POST /inspection/facility-list — { facilityId }
export const inspectionFacilityListSchema = z.object({
    facilityId: z.string().min(1, "Valid facility ID is required"),
});
// POST /inspection/accept and /inspection/change — { inspectionId, inspectionDate, timeOfDay, ... }
// passthrough keeps compliedWithFacility (and any other extras) untouched for the service.
export const acceptInspectionSchema = z
    .object({
    inspectionId: inspectionIdField,
    inspectionDate: ymdDateSchema,
    timeOfDay: z.boolean({ invalid_type_error: "Time of day is required" }),
})
    .passthrough();
// POST /inspection/update-due-date — { inspectionId, dueDate }
export const updateInspectionDueDateSchema = z.object({
    inspectionId: inspectionIdField,
    dueDate: ymdDateSchema,
});
// POST /inspection/update-status — { inspectionId, newStatus }
export const updateInspectionStatusSchema = z.object({
    inspectionId: inspectionIdField,
    newStatus: statusTypeSchema,
});
// POST /inspection/relevant-fields — { actionListItemId, type? }
export const inspectionRelevantFieldsSchema = z.object({
    actionListItemId: z.string().min(1, "actionListItemId is required"),
    type: z.enum(["prequalification", "actionList"]).optional(),
});
// POST /inspection/by-prequalification — { prequalificationId }
export const inspectionByPrequalificationSchema = z.object({
    prequalificationId: z.string().min(1, "prequalificationId is required"),
});
/**
 * Inspection protocol validators (Begehungsprotokoll). The form is saved as a whole; its shape is
 * `ProtocolData` (inspectionProtocol/definitions.ts). Item keys are the catalog's V-codes
 * ("V17") or the key of an item the walker added. Photo ids are generated in the browser, so a
 * retried upload of the same photo is recognised instead of being stored twice. These schemas
 * only bound the input; what a complete protocol needs is `summarizeProtocol`'s job.
 */
const protocolItemKeyField = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9._-]{1,64}$/, "Invalid item key");
const protocolCodeField = z
    .string()
    .trim()
    .regex(/^V\d{1,3}$/i, "Invalid requirement code")
    .transform(code => code.toUpperCase());
const protocolPhotoIdField = z.string().uuid("Invalid photo ID");
const protocolInstantField = z.string().datetime({ offset: true, message: "Invalid date" });
const protocolSignatureImageField = z
    .string()
    .max(400000, "Die Unterschrift ist zu groß")
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, "Invalid signature image");
const protocolAttendanceField = z.enum(["PRESENT", "ABSENT", "LEFT"]).nullish();
const protocolIdentificationField = z
    .enum(["ID_CARD", "PASSPORT", "DRIVERS_LICENSE", "KNOWN"])
    .nullish();
export const inspectionProtocolDataSchema = z.object({
    facilityKind: z.enum(["MAIN", "BRANCH"]).nullish(),
    items: z
        .record(protocolItemKeyField, z.object({
        answer: z.enum(["YES", "NO"]).nullish(),
        values: z.record(z.string().max(64), z.string().max(500, "Eingabe zu lang")).optional(),
        note: z.string().max(2000, "Bemerkung zu lang").nullish(),
        photoIds: z.array(protocolPhotoIdField).max(30, "Höchstens 30 Fotos je Punkt").optional(),
    }))
        .optional(),
    extraItems: z
        .array(z.object({
        key: protocolItemKeyField,
        code: protocolCodeField.nullish(),
        title: z.string().trim().min(1, "Bezeichnung fehlt").max(300),
    }))
        .max(50)
        .optional(),
    persons: z
        .record(z.string().max(64), z.object({
        attendance: protocolAttendanceField,
        identification: protocolIdentificationField,
    }))
        .optional(),
    extraPersons: z
        .array(z.object({
        key: protocolItemKeyField,
        role: z.enum(["SPECIALIST", "COMPANION"]),
        name: z.string().max(200),
        attendance: protocolAttendanceField,
        identification: protocolIdentificationField,
    }))
        .max(10)
        .optional(),
    findings: z.string().max(5000, "Feststellungen zu lang").nullish(),
    signatures: z
        .object({
        walker: z
            .object({ image: protocolSignatureImageField, signedAt: protocolInstantField })
            .nullish(),
        // The name of whoever signs for the facility is typed before they sign
        facility: z
            .object({
            image: protocolSignatureImageField.nullish(),
            name: z.string().trim().max(200),
            signedAt: protocolInstantField.nullish(),
        })
            .nullish(),
    })
        .optional(),
});
// POST /inspection-protocol/save — { inspectionId, baseRevision, data }: the revision the draft
// was based on, so a stale draft can't overwrite a newer save
export const inspectionProtocolSaveSchema = z.object({
    inspectionId: inspectionIdField,
    baseRevision: z.number().int().min(0),
    data: inspectionProtocolDataSchema,
});
// POST /inspection-protocol/upload-photo — the multipart fields sent next to the file
export const inspectionProtocolPhotoSchema = z.object({
    inspectionId: inspectionIdField,
    itemKey: protocolItemKeyField,
    photoId: protocolPhotoIdField,
    // When the photo was taken (UTC ISO); uploads can happen hours later
    takenAt: protocolInstantField.optional(),
});
// POST /inspection-protocol/submit — { inspectionId, revision }: the revision the walker reviewed
export const inspectionProtocolSubmitSchema = z.object({
    inspectionId: inspectionIdField,
    revision: z.number().int().min(1, "Das Protokoll ist noch nicht gespeichert"),
});
/**
 * Specialist validators — IDs are String(36). `specialist` is a passthrough object; `providingFields`
 * is left untyped (z.any) since its shape is owned by the service.
 */
const specialistIdField = z.string().min(1, "Specialist ID is required");
// POST /specialist/create — { specialist }
export const createSpecialistSchema = z.object({
    specialist: z.object({}).passthrough(),
});
// POST /specialist/get — { id, includeInactive?, prequalificationId?, prequalificationOnly? }
export const getSpecialistSchema = z.object({
    id: z.string().min(1, "Facility ID is required"),
    includeInactive: z.boolean().optional(),
    prequalificationId: z.string().nullish(),
    prequalificationOnly: z.boolean().optional(),
});
// POST /specialist/delete — { specialistId, date? }
export const deleteSpecialistSchema = z.object({
    specialistId: specialistIdField,
    date: z.string().nullish(),
});
// POST /specialist/move — { specialistId, targetFacilityId }
export const moveSpecialistSchema = z.object({
    specialistId: specialistIdField,
    targetFacilityId: z.string().min(1, "Target facility ID is required"),
});
// POST /specialist/edit — { specialist }
export const editSpecialistSchema = z.object({
    specialist: z.object({}).passthrough(),
});
// POST /specialist/specific — { id }
export const specificSpecialistSchema = z.object({
    id: z.string().min(1, "Specialist ID is required"),
});
// POST /specialist/update — { id, specialist, forceLatestCatalog? }
export const updateSpecialistSchema = z.object({
    id: z.string().min(1, "Specialist ID is required"),
    specialist: z.object({}).passthrough(),
    forceLatestCatalog: z.boolean().optional(),
});
// POST /specialist/updateOccupation — { id, specialist }
export const updateSpecialistOccupationSchema = z.object({
    id: z.string().min(1, "Specialist ID is required"),
    specialist: z.object({}).passthrough(),
});
// POST /specialist/check-existence — { occupationId, facilityId, firstName, lastName }
export const checkSpecialistExistenceSchema = z.object({
    occupationId: z.string().min(1, "Occupation ID is required"),
    facilityId: z.string().min(1, "Facility ID is required"),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
});
// POST /specialist/add-providing-fields — { specialistId, providingFields }
export const addProvidingFieldsSchema = z.object({
    specialistId: specialistIdField,
    providingFields: z.any(),
});
// POST /specialist/connect-to-prequalification and /disconnect-from-prequalification
export const specialistPrequalificationSchema = z.object({
    specialistId: specialistIdField,
    prequalificationId: z.string().min(1, "Prequalification ID is required"),
});
// POST /specialist/connect-to-facility — { specialistId, facilityId }
export const connectSpecialistToFacilitySchema = z.object({
    specialistId: specialistIdField,
    facilityId: z.string().min(1, "Facility ID is required"),
});
// POST /specialist/updateSpecialistActiveState — { specialistId, active }
export const updateSpecialistActiveStateSchema = z.object({
    specialistId: specialistIdField,
    active: z.boolean({ invalid_type_error: "Active state must be a boolean" }),
});
/**
 * Prequalification validators
 *
 * This domain has many endpoints. Where the controller previously did no validation, the schema
 * only requires the primary id and `.passthrough()`es the rest (non-breaking). Where the controller
 * had inline checks, the schema reproduces them. `requiredValue` mirrors the controller's `if (!x)`
 * truthy guard for fields whose exact shape is owned by the service.
 */
const preqIdField = z.string().min(1, "Prequalification ID is required");
const requiredValue = (message) => z.any().refine(value => Boolean(value), { message });
// POST /prequalification/list — loose object body
export const listPrequalificationSchema = z.object({}).passthrough();
// { facilityId } — POST /get and /facilityList
export const prequalificationFacilityIdSchema = z.object({
    facilityId: z.string().min(1, "Facility ID is required"),
});
// { prequalificationId } — POST /specific and /revertCriteriaCatalogVersion
export const prequalificationIdBodySchema = z.object({
    prequalificationId: preqIdField,
});
// { actionListItemId, prequalificationId } — POST /acceptRequest and /rejectRequest
export const actionListPrequalificationSchema = z
    .object({
    actionListItemId: z.string().min(1, "actionListItemId is required"),
    prequalificationId: preqIdField,
})
    .passthrough();
// POST /prequalification/request — { prequalificationId, facilityId }
export const requestPrequalificationSchema = z
    .object({
    prequalificationId: preqIdField,
    facilityId: z.string().min(1, "Facility ID is required"),
})
    .passthrough();
/**
 * Who the main contact person of a Re-PQ request is (`requalificationData.contactPersonMode`):
 *
 * - `current`: the stored main contact stays; `contactPerson` holds their details as edited in the
 *   wizard (`gender`, `firstName`, `lastName`, `phoneNumber`, `faxNumber`, `email`).
 * - `change`: another contact person of the facility takes over; `contactPerson.id` names them.
 * - `new`: a new contact person takes over; `contactPerson` holds their details.
 *
 * The wizard used until 2026-10 sent `add` for a new person, filled with whatever its broken
 * fields had caught. That mode is refused, so a tab still running that build renames nobody.
 */
export const RePqContactMode = { CURRENT: "current", CHANGE: "change", NEW: "new" };
const isBlankText = (value) => typeof value !== "string" || !value.trim();
const isEmailAddress = (value) => z.string().trim().email().safeParse(value).success;
// POST /prequalification/requestRePreQualification — { requalificationData: {...} }
// NOTE: the frontend also sends `agreements`, but the backend never reads it, so it is not
// validated here — it passes through harmlessly. Don't require fields the service ignores.
export const requestRePrequalificationSchema = z.object({
    requalificationData: z
        .object({
        facilityId: z.string().min(1, "Betriebsstätte nicht hinterlegt"),
        // `facilityData.ikNumber` is written straight back onto the facility, so it carries
        // the same rule as any other IK write (see requestRePrequalification.js).
        facilityData: z
            .object({ ikNumber: optionalIkNumberField }, {
            required_error: "Betriebsstättendaten nicht hinterlegt",
            invalid_type_error: "Betriebsstättendaten nicht hinterlegt",
        })
            .passthrough(),
        contactPersonMode: z
            .enum([RePqContactMode.CURRENT, RePqContactMode.CHANGE, RePqContactMode.NEW], {
            errorMap: () => ({
                message: "Diese Ansicht ist veraltet. Bitte laden Sie die Seite neu.",
            }),
        })
            .optional(),
        contactPerson: requiredValue("Ansprechpartner nicht hinterlegt"),
        specialists: z.array(z.any()).min(1, "Fachliche Leitungen nicht hinterlegt"),
        bankingData: requiredValue("Bankdaten nicht hinterlegt"),
    })
        .passthrough()
        .superRefine((data, ctx) => {
        const person = data.contactPerson;
        // A missing person is reported by its own check
        if (!person || typeof person !== "object")
            return;
        const issue = (field, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["contactPerson", field], message });
        const mode = data.contactPersonMode ?? RePqContactMode.CURRENT;
        if (mode === RePqContactMode.CHANGE) {
            if (isBlankText(person.id)) {
                issue("id", "Bitte wählen Sie die neue Hauptkontaktperson aus.");
            }
            return;
        }
        // The details are written onto a person record, so a name is never blanked
        if (isBlankText(person.firstName))
            issue("firstName", "Vorname der Hauptkontaktperson fehlt");
        if (isBlankText(person.lastName))
            issue("lastName", "Nachname der Hauptkontaktperson fehlt");
        // A new person is checked as contactpersons/create checks one. The stored main contact's
        // address isn't: the wizard's own check is laxer than this one, and a stored address it
        // accepted must not block the request.
        if (mode === RePqContactMode.NEW) {
            if (!isEmailAddress(person.email)) {
                issue("email", "Ungültige E-Mail-Adresse der Hauptkontaktperson");
            }
            if (![0, 1, 2].includes(person.gender)) {
                issue("gender", "Anrede der Hauptkontaktperson fehlt");
            }
        }
    }),
});
// POST /prequalification/updateListItem — { id, ... }
export const updatePrequalificationItemSchema = z
    .object({ id: z.string().min(1, "ID is required") })
    .passthrough();
// POST /prequalification/updateItemActive — { id, active }
export const updatePrequalificationItemActiveSchema = z.object({
    id: z.string().min(1, "ID is required"),
    active: z.boolean({ invalid_type_error: "Active state must be a boolean" }),
});
// POST /prequalification/moveImage — { imageId, destinationId, preqItemId }
export const movePrequalificationImageSchema = z.object({
    imageId: z.string().min(1, "imageId is required"),
    destinationId: z.string().min(1, "destinationId is required"),
    preqItemId: z.string().min(1, "preqItemId is required"),
});
// POST /prequalification/declineRequest, /specificItemList, /createPreqItemComment,
// /createPreqItemImageComment, /facilitySpecificList — loose object bodies
export const prequalificationLooseBodySchema = z.object({}).passthrough();
// POST /prequalification/pushToReview — { adminId, id, ... }
export const pushToReviewSchema = z
    .object({
    adminId: z.string().min(1, "adminId is required"),
    id: z.string().min(1, "id is required"),
})
    .passthrough();
// { id } + extras — POST /failedReview, /successPrequalification, /rejectPrequalification
export const prequalificationIdItemSchema = z
    .object({ id: z.string().min(1, "ID is required") })
    .passthrough();
// POST /prequalification/changeCommentReadableStatus — { commentId, status }
export const changeCommentReadableStatusSchema = z.object({
    commentId: z.string().min(1, "Comment ID is required"),
    status: z.boolean({ invalid_type_error: "status must be a boolean" }),
});
// POST /prequalification/changePreqComment — { commentId, text }
export const changePreqCommentSchema = z.object({
    commentId: z.string().min(1, "Comment ID is required"),
    text: z.string().min(1, "text is required"),
});
// POST /prequalification/changeImageCommentReadableStatus — { imageId, commentId, readable }
export const changeImageCommentReadableStatusSchema = z
    .object({
    imageId: z.string().min(1, "imageId is required"),
    commentId: z.string().min(1, "Comment ID is required"),
    readable: z.boolean({ invalid_type_error: "readable must be a boolean" }),
})
    .passthrough();
// { commentId } + extras — POST /changePreqCommentReadableStatus and /deletePreqComment
export const commentIdBodySchema = z
    .object({ commentId: z.string().min(1, "Comment ID is required") })
    .passthrough();
// POST /prequalification/specificPrequalificationItem — { prequalificationItemId }
export const specificPrequalificationItemSchema = z.object({
    prequalificationItemId: z.string().min(1, "Prequalification Item ID is required"),
});
// POST /prequalification/changeCriteriaCatalogVersion — { prequalificationId, newVersion }
export const changeCriteriaCatalogVersionSchema = z.object({
    prequalificationId: preqIdField,
    newVersion: requiredValue("Prequalification ID and criteria catalog version are required"),
});
// POST /prequalification/copyImageToFacility — { imageId, ... }
export const copyImageToFacilitySchema = z
    .object({ imageId: z.string().min(1, "imageId is required") })
    .passthrough();
// POST /prequalification/updatePrequalificationDates — { prequalificationId, formattedDates }
export const updatePrequalificationDatesSchema = z.object({
    prequalificationId: preqIdField,
    formattedDates: requiredValue("Prequalification ID and formatted dates are required"),
});
// POST /prequalification/updatePrequalificationStatus — { prequalificationId, oldStatus, newStatus }
export const updatePrequalificationStatusSchema = z.object({
    prequalificationId: preqIdField,
    oldStatus: z.string().min(1, "oldStatus is required"),
    newStatus: z.string().min(1, "newStatus is required"),
});
// POST /prequalification/refreshPrequalificationItems — { processId }
export const refreshPrequalificationItemsSchema = z.object({
    processId: z.string().min(1, "Process ID is required"),
});
// POST /prequalification/addItems — { prequalificationId, processType?, items }
export const addPrequalificationItemsSchema = z
    .object({
    prequalificationId: preqIdField,
    items: requiredValue("Prequalification ID and items are required"),
})
    .passthrough();
// POST /prequalification/deleteItem — { itemId }
export const deletePrequalificationItemSchema = z.object({
    itemId: z.string().min(1, "Item ID is required"),
});
// POST /prequalification/updatePrequalificationActiveState — { prequalificationId, active }
export const updatePrequalificationActiveStateSchema = z.object({
    prequalificationId: preqIdField,
    active: z.boolean({ invalid_type_error: "Active state must be a boolean" }),
});
/**
 * Assessment validators — IDs are String(36). The create service only uses facilityId (its other
 * args are ignored), so only facilityId is required there. GET /all (query-only) stays unvalidated.
 */
const assessmentIdField = z.string().min(1, "assessmentId is required");
// POST /assessment/create — { facilityId, ... }
export const createAssessmentSchema = z
    .object({ facilityId: z.string().min(1, "facilityId is required") })
    .passthrough();
// { facilityId } — POST /get and /open-without-inspection
export const assessmentFacilityIdSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
});
// { assessmentId } — POST /specific, /bill-list, /certificate, /refreshAssessmentItems
export const assessmentIdBodySchema = z.object({
    assessmentId: assessmentIdField,
});
// POST /assessment/reject — { assessmentId, comment?, includeEmail? }
export const rejectAssessmentSchema = z.object({ assessmentId: assessmentIdField }).passthrough();
// POST /assessment/success — { assessmentId, content: { startDate?, endDate?, comment?, sendXML? } }
// The dates go onto the certificate and into the GKV report, so they must be calendar dates.
const certificateDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum muss im Format JJJJ-MM-TT übergeben werden")
    .nullish();
export const successAssessmentSchema = z.object({
    assessmentId: assessmentIdField,
    content: z
        .object({ startDate: certificateDateSchema, endDate: certificateDateSchema }, { required_error: "assessmentId and content are required" })
        .passthrough(),
});
// POST /assessment/send — { assessmentId, assessmentType }
export const sendAssessmentSchema = z.object({
    assessmentId: z.string().min(1, "Änderungs / Überwachungs ID und Typ erforderlich"),
    assessmentType: z.string().min(1, "Änderungs / Überwachungs ID und Typ erforderlich"),
});
// POST /assessment/createReview — { facilityId, type }
// `type` must be a known monitoring type: the service maps it via AssessmentType[type] and
// silently treats anything unrecognized as a second monitoring, so reject bad values here.
export const createReviewAssessmentSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
    type: z.enum(["FIRSTMONITORING", "SECONDMONITORING"], {
        message: "type must be FIRSTMONITORING or SECONDMONITORING",
    }),
});
// POST /assessment/facility-list — { facilityId, includeCompleted?, includeChanges? }
export const assessmentFacilityListSchema = z
    .object({ facilityId: z.string().min(1, "facilityId is required") })
    .passthrough();
// POST /assessment/combine — { combinedData, userId? }
export const combineAssessmentSchema = z
    .object({ combinedData: requiredValue("combinedData is required") })
    .passthrough();
// POST /assessment/change-status — { assessmentId, newStatus }
export const changeAssessmentStatusSchema = z.object({
    assessmentId: assessmentIdField,
    newStatus: z.string().min(1, "newStatus is required"),
});
// POST /assessment/change-active — { assessmentId, isActive }
export const changeAssessmentActiveSchema = z.object({
    assessmentId: assessmentIdField,
    isActive: z.boolean({ invalid_type_error: "assessmentId and isActive are required" }),
});
// POST /assessment/merge — { assessmentIds } (array; any length, matching the controller)
export const mergeAssessmentsSchema = z.object({
    assessmentIds: z.array(z.any(), {
        required_error: "assessmentIds array is required",
        invalid_type_error: "assessmentIds array is required",
    }),
});
// POST /assessment/edit-changes — { assessmentId, updatedChanges }
export const editAssessmentChangesSchema = z.object({
    assessmentId: assessmentIdField,
    updatedChanges: requiredValue("assessmentId and updatedChanges are required"),
});
/**
 * Account validators. These mirror the controller's inline checks. Role-based guards stay in the
 * controller (e.g. walker-only notification preferences) since they read res.locals.user, not the
 * body. GET list endpoints and the path-param `details/:accountId` route need no body schema.
 */
// POST /account/update — { id, details }; details.error === true signals a client build error
export const updateAccountSchema = z
    .object({
    id: requiredValue("Missing id or details in request body"),
    details: requiredValue("Missing id or details in request body"),
})
    .refine(({ details }) => details?.error !== true, {
    message: "Fehler beim Aktualisieren des Accounts",
    path: ["details"],
});
// POST /account/create-admin — { account: { … } } with conditional walker-only fields
export const createAdminSchema = z.object({
    account: z
        .object({
        email: z.string().trim().min(1, "Email is required"),
        password: z.string().trim().min(1, "Password is required"),
        first_name: z.string().trim().min(1, "First name is required"),
        last_name: z.string().trim().min(1, "Last name is required"),
        gender: z.any().refine(v => v !== null && v !== undefined, { message: "Gender is required" }),
        admin: z.boolean().optional(),
        walker: z.boolean().optional(),
        superAdmin: z.boolean().optional(),
        post_code: z.union([z.string(), z.number()]).optional(),
        postCodeArea: z.string().optional(),
        scope: z.string().optional(),
    })
        .passthrough()
        .superRefine((acc, ctx) => {
        if (!acc.admin && !acc.walker && !acc.superAdmin) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "At least one role must be selected",
                path: ["admin"],
            });
        }
        if (acc.walker) {
            if (!acc.post_code?.toString().trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Post code is required for walker role",
                    path: ["post_code"],
                });
            }
            if (!acc.postCodeArea?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Post code area is required for walker role",
                    path: ["postCodeArea"],
                });
            }
            if (!acc.scope?.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Scope is required for walker role",
                    path: ["scope"],
                });
            }
        }
    }),
});
// POST /account/revive — { reviveData }
// `company.ikNumber` / `facility.ikNumber` are written straight onto the new rows, so they
// carry the same IK rule as every other write path (see account/reviveAccount.js).
export const reviveAccountSchema = z
    .object({
    reviveData: z
        .object({
        company: z.object({ ikNumber: ikNumberField }).passthrough(),
        facility: z.object({ ikNumber: optionalIkNumberField }).passthrough().optional(),
    })
        .passthrough(),
})
    .passthrough();
// POST /account/update-self — the logged-in account's own name, Anrede and address, nothing
// else: a role flag, `verified` or a password in the body is refused. Without `gender` the stored
// one stays (a session stored before the login returned it doesn't know it). A new address needs
// the current password (the service checks it, since only it knows the stored address).
export const updateOwnAccountSchema = z
    .object({
    firstName: requiredText("Vorname ist erforderlich"),
    lastName: requiredText("Nachname ist erforderlich"),
    gender: genderField.optional(),
    email: z.string().trim().email("Ungültige E-Mail-Adresse"),
    currentPassword: z.string().optional(),
})
    .strict("Hier lassen sich nur Name, Anrede und E-Mail-Adresse ändern.");
// PATCH /account/notification-preferences — { inspection_notification_preference }
export const updateNotificationPreferencesSchema = z.object({
    inspection_notification_preference: z.enum(["IMMEDIATE", "DAILY_DIGEST"], {
        errorMap: () => ({
            message: "Invalid notification preference. Must be IMMEDIATE or DAILY_DIGEST",
        }),
    }),
});
// POST /account/update-evaluator — { eventId, selectedAccountId, modalType, isAssessment? }
export const updateEvaluatorSchema = z
    .object({
    eventId: requiredValue("Missing required fields"),
    selectedAccountId: requiredValue("Missing required fields"),
    modalType: requiredValue("Missing required fields"),
})
    .passthrough();
// POST /account/add-company and /remove-company — { accountId, companyId }
export const accountCompanySchema = z.object({
    accountId: requiredValue("Missing accountId or companyId"),
    companyId: requiredValue("Missing accountId or companyId"),
});
// POST /account/delete-account — { accountId }
export const deleteAccountSchema = z.object({
    accountId: requiredValue("Missing accountId"),
});
/**
 * Catalog item validators for reading. The writes live with the other catalog validators above.
 * POST /catalog-items/list takes no body.
 */
// POST /catalog-items/specific — { id }
export const catalogItemIdSchema = z.object({
    id: z.string().min(1, "id is required"),
});
/**
 * Substitute validators. POST /substitute/create forwards the whole body to the service, so the
 * schema is `.passthrough()`. Update/delete identify the substitute via a path param (guaranteed by
 * routing) and list takes no body, so those need no schema.
 */
// POST /substitute/create — { email, password, firstName, lastName, gender, … }
export const createSubstituteSchema = z
    .object({
    email: z.string().trim().min(1, "Email is required"),
    password: z.string().trim().min(1, "Password is required"),
    firstName: z.string().trim().min(1, "First name is required"),
    lastName: z.string().trim().min(1, "Last name is required"),
    gender: z.any().refine(v => v !== null && v !== undefined, { message: "Gender is required" }),
})
    .passthrough();
/**
 * Providing-fields validators. Only /get needs a body schema (it fetches a specific provider by id);
 * /list takes an optional idList that may mean "list all", so it stays unvalidated.
 */
// POST /providing-fields/get — { id, includeInactive? }
export const providingFieldsGetSchema = z
    .object({ id: z.string().min(1, "id is required") })
    .passthrough();
/**
 * Statistics validators. `requiredValue` mirrors the controller's `!x` truthy guards (scopeId /
 * month / year / quarter may arrive as numbers). GET /customerStatistics takes no body.
 */
// POST /statistics/facilitiesByScope — { scopeId }
export const facilitiesByScopeSchema = z.object({
    scopeId: requiredValue("scopeId is required"),
});
// POST /statistics/getWalkerStats — { startDate, endDate } (inclusive "YYYY-MM-DD" range)
export const walkerStatsSchema = z.object({
    startDate: requiredValue("Start date and end date are required"),
    endDate: requiredValue("Start date and end date are required"),
});
// POST /statistics/getMonitoringStats — { quarter, year }
export const monitoringStatsSchema = z.object({
    quarter: requiredValue("Quarter and year are required"),
    year: requiredValue("Quarter and year are required"),
});
/**
 * XML validators. ids are String(36). The create-* endpoints share the
 * "prequalificationId + (endDate|startDate)" guard. /extractXMLContent is multipart (file in
 * req.file, not body), so it stays unvalidated. A suspension is reported through
 * /facilities/createSuspension (createSuspensionSchema).
 */
// POST /xml/getXMLContent, /downloadXML, /resendReport and /sentReport — { id }
export const xmlIdSchema = z.object({
    id: z.string().min(1, "xmlFileId is required"),
});
// POST /xml/createPreviewXML, /createCorrectionXML, /createInitialXML
//   — { prequalificationId, endDate?, startDate?, type? }
export const xmlDateRangeSchema = z
    .object({
    prequalificationId: z.string().min(1, "prequalificationId is required"),
    endDate: z.any().optional(),
    startDate: z.any().optional(),
})
    .passthrough()
    .refine(b => Boolean(b.endDate) || Boolean(b.startDate), {
    message: "endDate or startDate is required",
    path: ["endDate"],
});
// POST /xml/createWithdrawalXML — { prequalificationId, endDate, isAfterSuspension? }
export const createWithdrawalXMLSchema = z
    .object({
    prequalificationId: z.string().min(1, "prequalificationId is required"),
    endDate: requiredValue("endDate is required"),
})
    .passthrough();
// POST /xml/getFacilityXMLList and /sentReports — { facilityId }
export const xmlFacilityListSchema = z.object({
    facilityId: z.string().min(1, "facilityId is required"),
});
/**
 * Invoice (billing) validators. These mirror the controller's `!x` truthy guards exactly via
 * `requiredValue` (billing ids are strings but the guards are truthy, so this is the faithful
 * translation). `.passthrough()` is used wherever the controller forwards extra optional fields
 * (e.g. `active`, `allowDuplicate`, the collective-invoice metadata). POST /get, /list and
 * /createStripeTaxRate have no inline checks (list's companyId may be optional) and stay unvalidated.
 */
// POST /invoice/create-invoice — { facilityId, billingType, onlyCreateInvoiceForSubscription? }
export const createInvoiceSchema = z
    .object({
    facilityId: requiredValue("facilityId and billingType are required"),
    billingType: requiredValue("facilityId and billingType are required"),
})
    .passthrough();
// { collectiveInvoiceId } — /create-collective-bill, /renew-collective-bill,
//   /send-collective-bill-email, /reset-collective-bill
export const invoiceCollectiveIdSchema = z.object({
    collectiveInvoiceId: requiredValue("collectiveInvoiceId is required"),
});
// { invoiceId } — /delete-bill, /get-invoice-image
export const invoiceIdSchema = z.object({
    invoiceId: requiredValue("invoiceId is required"),
});
// POST /invoice/change-bill-active-state — { invoiceId, active? }
export const changeBillActiveStateSchema = z
    .object({ invoiceId: requiredValue("invoiceId is required") })
    .passthrough();
// { companyId } — /create-yearly-basic-collective, /get-facility-billing-status
export const invoiceCompanyIdSchema = z.object({
    companyId: requiredValue("companyId is required"),
});
// POST /invoice/addInvoiceToCollectiveInvoice — { facilityId, collectiveInvoiceId, allowDuplicate? }
export const addInvoiceToCollectiveSchema = z
    .object({
    facilityId: requiredValue("facilityId and collectiveInvoiceId are required"),
    collectiveInvoiceId: requiredValue("facilityId and collectiveInvoiceId are required"),
})
    .passthrough();
// POST /invoice/removeInvoiceFromCollectiveInvoice — { invoiceId, collectiveInvoiceId }
export const removeInvoiceFromCollectiveSchema = z.object({
    invoiceId: requiredValue("invoiceId and collectiveInvoiceId are required"),
    collectiveInvoiceId: requiredValue("invoiceId and collectiveInvoiceId are required"),
});
// PATCH /invoice/update-collective-invoice
//   — { collectiveInvoiceId, date?, due_date?, payment_due_date?, invoice_billing_type?, visible_for_customer? }
export const updateCollectiveInvoiceSchema = z
    .object({ collectiveInvoiceId: requiredValue("collectiveInvoiceId is required") })
    .passthrough();
// POST /invoice/transfer-invoice-to-collective — { invoiceId, targetCollectiveInvoiceId }
export const transferInvoiceToCollectiveSchema = z.object({
    invoiceId: requiredValue("invoiceId and targetCollectiveInvoiceId are required"),
    targetCollectiveInvoiceId: requiredValue("invoiceId and targetCollectiveInvoiceId are required"),
});
/**
 * Media validators (JSON endpoints only). The 6 multipart upload routes validate `req.file` in the
 * controller (out of `validateRequest`'s body scope) and stay as-is; endpoints with no inline check
 * (deleteFile/getS3Object/getBillList/getCertificate/createStatusReport) keep passing their body
 * straight through. `requiredValue` mirrors the `!x`
 * guards; `.passthrough()` is used where the controller forwards extra optional fields.
 * /createPreviewCollectiveBill reuses `invoiceCollectiveIdSchema`.
 */
// POST /media/getSpecificFile — { imageId }
export const mediaImageIdSchema = z.object({
    imageId: requiredValue("Datei nicht gefunden"),
});
// POST /media/rotateImage — { imageId, degrees }
// `degrees` is a clockwise quarter turn. The service normalises the value (so -90 and 270 are the
// same turn) and re-checks it; this only keeps non-numeric and non-quarter-turn bodies out.
export const mediaRotateImageSchema = z.object({
    imageId: requiredValue("Keine Dokument-ID übergeben"),
    degrees: z
        .number({
        errorMap: () => ({ message: "Es kann nur um 90, 180 oder 270 Grad gedreht werden" }),
    })
        .int()
        .refine(d => d % 90 === 0 && d % 360 !== 0, {
        message: "Es kann nur um 90, 180 oder 270 Grad gedreht werden",
    }),
});
// POST /media/updateFileName — { id, fileName }
export const updateFileNameSchema = z.object({
    id: requiredValue("id and fileName are required"),
    fileName: requiredValue("id and fileName are required"),
});
// POST /media/majorCustomerBillList — { companyId, majorCustomerName }
export const majorCustomerBillListSchema = z.object({
    companyId: requiredValue("companyId and majorCustomerName are required"),
    majorCustomerName: requiredValue("companyId and majorCustomerName are required"),
});
// POST /media/specificCollectiveInvoice — { customerId }
export const mediaCustomerIdSchema = z.object({
    customerId: requiredValue("customerId is required"),
});
// POST /media/createPreviewCertificate and /createNewCertificate
//   — { prequalificationId, endDate?, startDate? } (only prequalificationId is guarded)
export const mediaCertificatePreviewSchema = z
    .object({
    prequalificationId: requiredValue("prequalificationId, endDate, and startDate are required"),
})
    .passthrough();
// POST /media/createPreviewXML — { prequalificationId, endDate?, startDate?, type? }
export const mediaPreviewXMLSchema = z
    .object({
    prequalificationId: requiredValue("prequalificationId, endDate, startDate, and type are required"),
})
    .passthrough();
// POST /media/billsByDateRange and /updateBilllistByDateRange — { startDate, endDate }
export const mediaDateRangeSchema = z.object({
    startDate: requiredValue("Start date and end date are required"),
    endDate: requiredValue("Start date and end date are required"),
});
// POST /media/billsByEntityId and /updateCompanyBillingList — { entityId, entityType }
export const mediaEntityIdSchema = z.object({
    entityId: requiredValue("Entity ID and entity type are required"),
    entityType: z.enum(["company", "facility"], {
        errorMap: () => ({ message: "Entity type must be 'company' or 'facility'" }),
    }),
});
// POST /media/billsByEntityAndDateRange — { entityId, entityType, startDate, endDate }
export const mediaEntityAndDateRangeSchema = z.object({
    entityId: requiredValue("Entity ID, entity type, start date, and end date are all required"),
    entityType: z.enum(["company", "facility"], {
        errorMap: () => ({ message: "Entity type must be 'company' or 'facility'" }),
    }),
    startDate: requiredValue("Entity ID, entity type, start date, and end date are all required"),
    endDate: requiredValue("Entity ID, entity type, start date, and end date are all required"),
});
// POST /media/renewBill and /sendBillEmail — { invoiceId, withEmail? }
export const mediaInvoiceIdSchema = z
    .object({ invoiceId: requiredValue("Invoice ID is required") })
    .passthrough();
// POST /media/updateFileStatus — { file }
export const updateFileStatusSchema = z.object({
    file: requiredValue("file is required"),
});
// POST /media/updateFileSubmitted — { id, submitted } (admin-only; toggles the "eingereicht" lock)
export const updateFileSubmittedSchema = z.object({
    id: requiredValue("Keine Dokument-ID übergeben"),
    submitted: z.boolean({
        errorMap: () => ({ message: "submitted muss ein Boolean sein" }),
    }),
});
